import { LoadingRows } from "../../UI/Loading/LoadingRows.tsx";
import { useMemo } from "react";
import dayjs from "dayjs";
import { TeacherCoordinatorReviewHistoryItem } from "./useTeacherCoordinatorReviewHistory.ts";
import { useTeacherStudentReviewHistory } from "./useTeacherStudentReviewHistory.ts";
import {
  compareScoreWindowWithPrevious,
  StudentSubScoreAverages,
} from "./studentReviewWindows.ts";
import {
  COORDINATOR_SCORE_WEIGHTS,
  CoordinatorReviewScores,
  STUDENT_SCORE_WINDOW_MONTHS,
} from "./teacherScoreAlgorithm.ts";
import { COORDINATOR_COLOR, STUDENT_COLOR } from "./teacherScoreColors.ts";
import { ScoreDelta } from "./ScoreDelta.tsx";

const MAX_SCORE = 5;

const STUDENT_SUB_SCORES: {
  key: keyof StudentSubScoreAverages;
  label: string;
}[] = [
  { key: "clearance", label: "Clearance" },
  { key: "involvement", label: "Involvement" },
  { key: "comfortable", label: "Comfortable" },
];

const COORDINATOR_SUB_SCORES: {
  key: keyof CoordinatorReviewScores;
  label: string;
}[] = [
  { key: "fluencyScore", label: "Fluency conditions" },
  { key: "presenceScore", label: "Role adaptation" },
  { key: "clarityScore", label: "Clarity" },
];

function SubScoreBar({
  label,
  hint,
  value,
  previousValue,
  color,
  fractionDigits,
}: {
  label: string;
  hint?: string;
  value: number | null;
  previousValue: number | null;
  color: string;
  fractionDigits: number;
}) {
  const filledPercentage = ((value ?? 0) / MAX_SCORE) * 100;

  return (
    <div className={"flex flex-col gap-1"}>
      <div className={"flex items-baseline justify-between gap-3"}>
        <span className={"text-xs text-slate-600"}>
          {label}
          {hint && <span className={"text-slate-400"}> · {hint}</span>}
        </span>
        <span className={"flex items-baseline gap-2"}>
          {value !== null && previousValue !== null && (
            <ScoreDelta
              current={value}
              previous={previousValue}
              fractionDigits={fractionDigits}
            />
          )}
          <span
            className={"text-sm font-semibold text-slate-800 w-9 text-right"}
          >
            {value === null ? "—" : value.toFixed(fractionDigits)}
          </span>
        </span>
      </div>
      <div className={"h-1.5 rounded-full bg-slate-100 overflow-hidden"}>
        <div
          className={"h-full rounded-full"}
          style={{ width: `${filledPercentage}%`, backgroundColor: color }}
        />
      </div>
    </div>
  );
}

function SubScoreGroup({
  title,
  description,
  children,
}: {
  title: string;
  description: string;
  children: React.ReactNode;
}) {
  return (
    <div className={"flex flex-col gap-3"}>
      <div>
        <p
          className={
            "text-xs font-semibold text-slate-500 uppercase tracking-wide"
          }
        >
          {title}
        </p>
        <p className={"text-xs text-slate-400"}>{description}</p>
      </div>
      {children}
    </div>
  );
}

function EmptySubScores({ message }: { message: string }) {
  return <p className={"text-sm text-slate-400"}>{message}</p>;
}

export function TeacherSubScoreBreakdown({
  teacherId,
  coordinatorReviews,
}: {
  teacherId: string;
  coordinatorReviews: TeacherCoordinatorReviewHistoryItem[];
}) {
  const { studentReviewGroups, loading } =
    useTeacherStudentReviewHistory(teacherId);
  const now = useMemo(() => dayjs(), []);

  const { scoreWindow, previousWindow } = useMemo(
    () => compareScoreWindowWithPrevious({ studentReviewGroups, now }),
    [studentReviewGroups, now],
  );

  const [latestCoordinatorReview, previousCoordinatorReview] =
    coordinatorReviews;

  return (
    <div className={"grid gap-x-10 gap-y-6 md:grid-cols-2"}>
      <SubScoreGroup
        title={"Students"}
        description={`Plain average of the last ${STUDENT_SCORE_WINDOW_MONTHS} months, compared with the previous ${STUDENT_SCORE_WINDOW_MONTHS}`}
      >
        {loading ? (
          <LoadingRows rows={3} rowClassName={"h-6"} />
        ) : scoreWindow.reviewCount === 0 ? (
          <EmptySubScores
            message={`No student reviews in the last ${STUDENT_SCORE_WINDOW_MONTHS} months.`}
          />
        ) : (
          STUDENT_SUB_SCORES.map(({ key, label }) => (
            <SubScoreBar
              key={key}
              label={label}
              value={scoreWindow.subScoreAverages[key]}
              previousValue={previousWindow.subScoreAverages[key]}
              color={STUDENT_COLOR}
              fractionDigits={2}
            />
          ))
        )}
      </SubScoreGroup>

      <SubScoreGroup
        title={"Coordinator"}
        description={
          latestCoordinatorReview
            ? `Latest review (${dayjs(latestCoordinatorReview.reviewedAtUtc).format("D MMM YYYY")}), compared with the previous one`
            : "Latest review, compared with the previous one"
        }
      >
        {!latestCoordinatorReview ? (
          <EmptySubScores message={"No coordinator reviews yet."} />
        ) : (
          COORDINATOR_SUB_SCORES.map(({ key, label }) => (
            <SubScoreBar
              key={key}
              label={label}
              hint={`weight ${Math.round(COORDINATOR_SCORE_WEIGHTS[key] * 100)}%`}
              value={latestCoordinatorReview.scores[key]}
              previousValue={previousCoordinatorReview?.scores[key] ?? null}
              color={COORDINATOR_COLOR}
              fractionDigits={0}
            />
          ))
        )}
      </SubScoreGroup>
    </div>
  );
}
